import { MAX_LINK_TEXT } from './linkText';
/**
 * # Link deception warnings (ADR 0101, plan/02 §3.6, #52)
 *
 * The confirm before a message link opens already shows the real destination
 * (ADR 0078 §8, ADR 0053). What it did not do was say when that destination is
 * *suspicious*: the link's text names one site and its href another, the host
 * is spelled with look-alike characters, it is a bare IP address, it hides the
 * real host behind `user@`, or it is a shortener whose destination nobody can
 * see. This module says so, as data the confirm and the URL preview render.
 *
 * Pure and dependency-free. The registrable-domain (eTLD+1) rule is a small
 * built-in table rather than the Public Suffix List plan/02 asks for: `tldts`
 * is ~30 KB gzip and only a transitive dependency here. The table errs towards
 * treating `xx.cc` as a suffix (GENERIC_SECOND_LEVEL below), because the
 * failure that matters is collapsing `bank.co.xx` and `evil.co.xx` to one
 * "site"; ADR 0101 records the trade-off.
 */

export type LinkWarning =
  /** The link text names one site and the href goes to another (both eTLD+1). */
  | { kind: 'mismatch'; shown: string; actual: string }
  /** A host with an IDN (`xn--`) label: `unicode` is how it would read. */
  | { kind: 'punycode'; unicode: string; ascii: string }
  /** A raw IPv4 or IPv6 host. */
  | { kind: 'ip'; host: string }
  /** `https://paypal.com@evil.example/`: everything before `@` is decoration. */
  | { kind: 'credentials'; host: string }
  /** A known URL shortener: the destination is not shown anywhere. */
  | { kind: 'shortener'; host: string };

// Defined in the frame protocol module, which the page's validator also
// reads; importing it from here pulled this module (and its tables) into the
// initial bundle (#577).
export { MAX_LINK_TEXT };

/**
 * Multi-label public suffixes, ICANN and private, where the last two labels
 * are not a registrable domain. Not the PSL: the suffixes real phishing
 * comparisons meet, plus the hosting platforms where one tenant can pose as
 * another (`paypal.github.io` is not PayPal's).
 */
const MULTI_LABEL_SUFFIXES = new Set([
  // ICANN second levels
  'co.uk', 'org.uk', 'me.uk', 'ltd.uk', 'plc.uk', 'net.uk', 'ac.uk', 'gov.uk', 'nhs.uk', 'police.uk',
  'com.au', 'net.au', 'org.au', 'edu.au', 'gov.au', 'asn.au', 'id.au',
  'co.nz', 'net.nz', 'org.nz', 'govt.nz', 'ac.nz',
  'co.jp', 'ne.jp', 'or.jp', 'ac.jp', 'go.jp', 'ad.jp', 'ed.jp', 'gr.jp', 'lg.jp',
  'com.br', 'net.br', 'org.br', 'gov.br', 'edu.br',
  'com.cn', 'net.cn', 'org.cn', 'gov.cn', 'edu.cn',
  'co.in', 'net.in', 'org.in', 'firm.in', 'gen.in', 'ind.in', 'gov.in', 'ac.in',
  'co.za', 'org.za', 'gov.za', 'ac.za', 'web.za',
  'com.mx', 'org.mx', 'gob.mx', 'edu.mx',
  'com.tr', 'org.tr', 'gov.tr', 'edu.tr',
  'com.sg', 'edu.sg', 'gov.sg', 'com.hk', 'org.hk', 'gov.hk', 'com.tw', 'org.tw', 'gov.tw',
  'co.kr', 'or.kr', 'go.kr', 'ac.kr', 'com.ar', 'gob.ar', 'com.co', 'gov.co', 'com.pe', 'com.ve',
  'co.il', 'org.il', 'gov.il', 'ac.il', 'com.my', 'gov.my', 'co.id', 'go.id', 'ac.id', 'or.id',
  'co.th', 'go.th', 'ac.th', 'in.th', 'com.ph', 'gov.ph', 'com.vn', 'gov.vn', 'com.pk', 'gov.pk',
  'com.sa', 'gov.sa', 'com.eg', 'gov.eg', 'com.ng', 'gov.ng', 'co.ke', 'go.ke', 'com.ua', 'gov.ua',
  'com.pl', 'net.pl', 'org.pl', 'gov.pl', 'co.at', 'or.at', 'gv.at', 'com.es', 'gob.es', 'com.pt',
  'com.gr', 'gov.gr', 'co.hu', 'com.ru', 'gov.ru',
  // Private: hosting where tenants are separate parties
  'github.io', 'gitlab.io', 'herokuapp.com', 'pages.dev', 'workers.dev', 'netlify.app', 'vercel.app',
  'web.app', 'firebaseapp.com', 'appspot.com', 'blogspot.com', 'wordpress.com', 'azurewebsites.net',
  'cloudfront.net', 'amazonaws.com', 'glitch.me', 'repl.co', 'onrender.com', 'fly.dev', 'ngrok.io',
  'ngrok-free.app', 'myshopify.com', 'wixsite.com', 'weebly.com', 'square.site', 'sharepoint.com',
  'r2.dev', 'translate.goog',
]);

/**
 * Second-level labels that, under a two-letter country code, are nearly
 * always a public suffix (`co.xx`, `com.xx`, `gov.xx`…). Used only when the
 * pair is not in the table, so an unlisted country still compares
 * `bank.co.xx` with `evil.co.xx` as two different sites.
 */
const GENERIC_SECOND_LEVEL = new Set([
  'co', 'com', 'net', 'org', 'gov', 'govt', 'edu', 'ac', 'or', 'ne', 'go', 'gob', 'gv', 'mil', 'ltd', 'plc', 'nic',
  'nom', 'sch', 'info', 'biz',
]);

/** URL shorteners: the destination is somewhere nobody has seen yet. */
const SHORTENERS = [
  'bit.ly', 'bitly.com', 't.co', 'tinyurl.com', 'goo.gl', 'ow.ly', 'is.gd', 'buff.ly', 'rebrand.ly', 'cutt.ly',
  'rb.gy', 'shorturl.at', 't.ly', 'tiny.cc', 'bl.ink', 'lnkd.in', 's.id', 'v.gd', 'qrco.de', 'trib.al',
  'dlvr.it', 'soo.gd', 'x.co', 'y2u.be', 'shorte.st', 'adf.ly', 'tiny.one', 'urlz.fr', 'clck.ru',
];

/**
 * Top-level domains bare link text may end in for this module to read it as
 * a host. Any two-letter country code counts; beyond those, only the common
 * generic ones, so "report.pdf", "photo.jpg" or "notes.docx" -- file names
 * that happen to parse as hosts -- never raise a mismatch. Text with a
 * scheme, a `www.` or an `@` is read whatever its TLD.
 */
const COMMON_GENERIC_TLDS = new Set([
  'com', 'net', 'org', 'info', 'biz', 'edu', 'gov', 'mil', 'int', 'io', 'ai', 'app', 'dev', 'xyz', 'online',
  'site', 'shop', 'store', 'top', 'club', 'live', 'tech', 'cloud', 'email', 'bank', 'money', 'support',
  'help', 'link', 'click', 'page', 'website', 'services', 'company', 'solutions', 'mobi', 'pro', 'name',
]);

const IPV4 = /^\d{1,3}(?:\.\d{1,3}){3}$/;

/** Lower-case, no trailing dot. */
function cleanHost(host: string): string {
  return host.toLowerCase().replace(/\.+$/, '');
}

/** True for a host `URL` serialised as an IP literal. */
function isIPHost(host: string): boolean {
  return host.startsWith('[') || IPV4.test(host);
}

/**
 * The registrable domain (eTLD+1) of an ASCII host: `www.paypal.co.uk` →
 * `paypal.co.uk`, `mail.example.com` → `example.com`. An IP literal, a single
 * label or a bare suffix comes back unchanged.
 */
export function registrableDomain(host: string): string {
  const clean = cleanHost(host);
  if (isIPHost(clean)) return clean;
  const labels = clean.split('.').filter((l) => l !== '');
  if (labels.length <= 2) return labels.join('.');
  const lastTwo = labels.slice(-2).join('.');
  const lastThree = labels.slice(-3).join('.');
  let suffixLabels = 1;
  if (MULTI_LABEL_SUFFIXES.has(lastThree)) suffixLabels = 3;
  else if (MULTI_LABEL_SUFFIXES.has(lastTwo)) suffixLabels = 2;
  else {
    const tld = labels[labels.length - 1] ?? '';
    const sld = labels[labels.length - 2] ?? '';
    if (tld.length === 2 && GENERIC_SECOND_LEVEL.has(sld)) suffixLabels = 2;
  }
  if (labels.length <= suffixLabels) return clean;
  return labels.slice(-(suffixLabels + 1)).join('.');
}

// --- punycode (RFC 3492), decode only -----------------------------------------

const BASE = 36;
const TMIN = 1;
const TMAX = 26;
const SKEW = 38;
const DAMP = 700;
const INITIAL_BIAS = 72;
const INITIAL_N = 128;
const MAXINT = 0x7fffffff;
/** A DNS name is at most 253 characters; nothing longer is decoded. */
const MAX_HOST = 253;

function adapt(delta: number, numPoints: number, firstTime: boolean): number {
  let d = firstTime ? Math.floor(delta / DAMP) : delta >> 1;
  d += Math.floor(d / numPoints);
  let k = 0;
  while (d > ((BASE - TMIN) * TMAX) >> 1) {
    d = Math.floor(d / (BASE - TMIN));
    k += BASE;
  }
  return k + Math.floor(((BASE - TMIN + 1) * d) / (d + SKEW));
}

function digitOf(code: number): number {
  if (code >= 0x30 && code <= 0x39) return code - 22; // 0-9 → 26-35
  if (code >= 0x41 && code <= 0x5a) return code - 0x41; // A-Z
  if (code >= 0x61 && code <= 0x7a) return code - 0x61; // a-z
  return BASE;
}

/** One label's punycode (without `xn--`) to Unicode, or null on any error. */
function decodeLabel(input: string): string | null {
  if (input === '') return null;
  const output: number[] = [];
  const delimiter = input.lastIndexOf('-');
  const basic = delimiter < 0 ? 0 : delimiter;
  for (let j = 0; j < basic; j++) {
    const code = input.charCodeAt(j);
    if (code >= 0x80) return null;
    output.push(code);
  }
  let n = INITIAL_N;
  let i = 0;
  let bias = INITIAL_BIAS;
  for (let index = basic > 0 ? basic + 1 : 0; index < input.length; ) {
    const oldi = i;
    let w = 1;
    for (let k = BASE; ; k += BASE) {
      if (index >= input.length) return null;
      const digit = digitOf(input.charCodeAt(index++));
      if (digit >= BASE) return null;
      if (digit > Math.floor((MAXINT - i) / w)) return null;
      i += digit * w;
      const t = k <= bias ? TMIN : k >= bias + TMAX ? TMAX : k - bias;
      if (digit < t) break;
      if (w > Math.floor(MAXINT / (BASE - t))) return null;
      w *= BASE - t;
    }
    const length = output.length + 1;
    bias = adapt(i - oldi, length, oldi === 0);
    if (Math.floor(i / length) > MAXINT - n) return null;
    n += Math.floor(i / length);
    i %= length;
    if (n > 0x10ffff || (n >= 0xd800 && n <= 0xdfff)) return null;
    output.splice(i, 0, n);
    i++;
  }
  return String.fromCodePoint(...output);
}

/**
 * The Unicode form of an ASCII host, for display beside the ASCII one. Never
 * throws: a label that does not decode (hostile or plain broken input) leaves
 * the whole host as it came.
 */
export function unicodeHost(host: string): string {
  if (host.length > MAX_HOST) return host;
  try {
    const labels = host.split('.');
    const out: string[] = [];
    for (const label of labels) {
      if (!/^xn--/i.test(label)) {
        out.push(label);
        continue;
      }
      const decoded = decodeLabel(label.slice(4));
      if (decoded === null) return host;
      out.push(decoded);
    }
    return out.join('.');
  } catch {
    return host;
  }
}

// --- link text ---------------------------------------------------------------

/** Wrapping a sender puts around an address in text: quotes, brackets. */
const LEADING_WRAP = /^[\s<([{"'“‘«]+/u;
const TRAILING_WRAP = /[\s>)\]}"'”’»,;:!?]+$/u;
const BARE_HOST = /^([^\s/@:?#]+\.[^\s/@:?#.]+)\.?(?:[/:?#]\S*)?$/u;
const EMAIL = /^[^\s@/]+@([^\s@/]+\.[^\s@/.]+)\.?$/u;

/** ASCII host the way `URL` serialises one, or null. */
function asciiHost(host: string): string | null {
  try {
    const parsed = new URL(`http://${host}/`);
    return parsed.hostname === '' ? null : cleanHost(parsed.hostname);
  } catch {
    return null;
  }
}

/**
 * The host the link's TEXT claims, when the text itself looks like an address:
 * a URL with a scheme, `www.something`, `name@domain`, or a bare host ending
 * in a country code or a common generic TLD. Anything else ("Example Shop",
 * "Click here", "report.pdf") claims nothing and is never compared.
 */
export function claimedHost(text: string | undefined): string | null {
  if (!text) return null;
  let t = text.slice(0, MAX_LINK_TEXT).trim().toLowerCase();
  t = t.replace(LEADING_WRAP, '').replace(TRAILING_WRAP, '');
  // A trailing full stop ends a sentence, not a host.
  t = t.replace(/\.$/, '');
  if (t === '' || /\s/u.test(t)) return null;
  if (/^https?:\/\//.test(t)) {
    try {
      const host = cleanHost(new URL(t).hostname);
      return host === '' ? null : host;
    } catch {
      return null;
    }
  }
  const email = EMAIL.exec(t);
  if (email?.[1]) return asciiHost(email[1]);
  const bare = BARE_HOST.exec(t);
  if (!bare?.[1]) return null;
  const host = bare[1];
  const tld = host.slice(host.lastIndexOf('.') + 1);
  const readable = host.startsWith('www.') || /^\p{L}{2}$/u.test(tld) || COMMON_GENERIC_TLDS.has(tld);
  if (!readable || /^[\d.]+$/.test(host)) return null;
  return asciiHost(host);
}

/** Collapses a link's text to what a reader sees, bounded (the frame does the same). */
export function linkTextOf(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw.replace(/\s+/gu, ' ').trim().slice(0, MAX_LINK_TEXT);
}

// --- the detector --------------------------------------------------------------

/**
 * What is suspicious about opening `url` from a link that reads `linkText`.
 * Without text (the URL preview), only what the URL alone shows. Ordered
 * most-telling first; empty for an ordinary link.
 */
export function linkWarnings(url: URL, linkText?: string): LinkWarning[] {
  const out: LinkWarning[] = [];
  const host = cleanHost(url.hostname);
  if (url.username !== '' || url.password !== '') {
    out.push({ kind: 'credentials', host });
  }
  const claimed = claimedHost(linkText);
  if (claimed !== null) {
    const shown = registrableDomain(claimed);
    const actual = registrableDomain(host);
    if (shown !== actual) out.push({ kind: 'mismatch', shown, actual });
  }
  if (host.split('.').some((label) => label.startsWith('xn--'))) {
    out.push({ kind: 'punycode', unicode: unicodeHost(host), ascii: host });
  }
  if (isIPHost(host)) out.push({ kind: 'ip', host });
  if (SHORTENERS.some((s) => host === s || host.endsWith(`.${s}`))) {
    out.push({ kind: 'shortener', host });
  }
  return out;
}

/**
 * One warning as a plain sentence, for `window.confirm` (the srcdoc renderer)
 * and as the accessible text of the confirm's list.
 */
export function warningSentence(w: LinkWarning): string {
  switch (w.kind) {
    case 'mismatch':
      return `The link text says ${w.shown}, but it goes to ${w.actual}.`;
    case 'punycode':
      return w.unicode === w.ascii
        ? `${w.ascii} is an international address that may imitate another one.`
        : `${w.unicode} is spelled with look-alike characters; its real address is ${w.ascii}.`;
    case 'ip':
      return `It goes to a bare IP address (${w.host}), not a named website.`;
    case 'credentials':
      return `The part before “@” is a disguise: this link goes to ${w.host}.`;
    case 'shortener':
      return `This is a shortened link (${w.host}); where it really goes isn’t shown.`;
  }
}

/** The few words the URL preview's badge uses for a warning. */
export function warningLabel(w: LinkWarning): string {
  switch (w.kind) {
    case 'mismatch':
      return 'text and address differ';
    case 'punycode':
      return 'look-alike characters';
    case 'ip':
      return 'bare IP address';
    case 'credentials':
      return 'disguised address';
    case 'shortener':
      return 'shortened link';
  }
}

/**
 * The srcdoc renderer's `window.confirm` text. Without warnings it is exactly
 * what it has always been (`reading.spec.ts` pins it).
 */
export function confirmMessage(url: URL, warnings: readonly LinkWarning[]): string {
  const question = `Open ${url.href}?`;
  if (warnings.length === 0) return question;
  return `${question}\n\nCareful:\n${warnings.map((w) => `• ${warningSentence(w)}`).join('\n')}`;
}
